// src/components/Cash/Cash.jsx

import React, { useState, useEffect, useContext } from 'react';
import { userContext } from '../../context/ContextProvider';
import cashService from '../../services/cashhService';
import CashTable from './CashTable';
import { showNotification } from '../../utils/notification';
import './cashh.css';

/**
 * Cash Management Component
 * Manages cash movements with type filtering (CASH, COLLECTION, CHEQUE)
 */
const Cash = ({ toggleSidebar }) => {
  // Get user info from context
  const { role, user } = useContext(userContext);
  
  // ========== STATE MANAGEMENT ==========
  const [allCashMovements, setAllCashMovements] = useState([]);
  const [filteredCashMovements, setFilteredCashMovements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeType, setActiveType] = useState('CASH'); // CASH, COLLECTION, CHEQUE
  const [filters, setFilters] = useState({
    search: '',
    salesPerson: '',
    inventory: '',
    startDate: '',
    endDate: getTodayDate()
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Get user role and ID from context
  const userRole = role;
  const userId = user?.id || user?.userId;

  // Get unique sales persons for dropdown
  const uniqueSalesPersons = React.useMemo(() => {
    const salesPersons = allCashMovements
      .map(c => c.salesPerson?.firstName)
      .filter(Boolean);
    return [...new Set(salesPersons)];
  }, [allCashMovements]);

  // Get unique inventories for dropdown
  const uniqueInventories = React.useMemo(() => {
    const inventories = allCashMovements
      .map(c => c.inventory?.name)
      .filter(Boolean);
    return [...new Set(inventories)];
  }, [allCashMovements]);

  // ========== LOAD DATA ON COMPONENT MOUNT ==========
  useEffect(() => {
    if (userRole) {
      loadCashMovements();
    }
  }, [userRole, userId]);

  // Auto-filter when filters or type changes
  useEffect(() => {
    applyFilters();
    setCurrentPage(1);
  }, [allCashMovements, filters, activeType]);

  // ========== DATA LOADING ==========
  const loadCashMovements = async () => {
    try {
      setIsLoading(true);
      
      if (!userRole) {
        throw new Error('User role is not available');
      }
      
      const data = await cashService.getAll();
      setAllCashMovements(data);
      showNotification(`Loaded ${data.length} cash movements`, 'success');
    } catch (error) {
      console.error('Error loading cash movements:', error);
      showNotification(`Failed to load cash movements: ${error.message}`, 'error');
      setAllCashMovements([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== FILTERING ==========
  const applyFilters = () => {
    const safeMovements = Array.isArray(allCashMovements) ? allCashMovements : [];
    let filtered = [...safeMovements];

    // Filter by type (CASH, COLLECTION, CHEQUE)
    filtered = filtered.filter(item => item.type === activeType);

    // Filter by sales person
    if (filters.salesPerson) {
      filtered = filtered.filter(item => {
        const salesPersonName = item.salesPerson?.firstName || '';
        return salesPersonName === filters.salesPerson;
      });
    }

    // Filter by inventory
    if (filters.inventory) {
      filtered = filtered.filter(item => {
        const inventoryName = item.inventory?.name || '';
        return inventoryName === filters.inventory;
      });
    }

    // Filter by search (description or reference)
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item => {
        const description = (item.description || '').toLowerCase();
        const reference = (item.reference || '').toLowerCase();
        const inventoryCode = (item.inventory?.code || '').toLowerCase();
        return description.includes(searchLower) || 
               reference.includes(searchLower) || 
               inventoryCode.includes(searchLower);
      });
    }

    // Filter by start date
    if (filters.startDate) {
      filtered = filtered.filter(item => {
        if (!item.date && !item.createdAt) return false;
        
        const dateValue = item.date || item.createdAt;
        const itemDate = new Date(dateValue);
        const startDate = new Date(filters.startDate);
        
        itemDate.setHours(0, 0, 0, 0);
        startDate.setHours(0, 0, 0, 0);
        
        return itemDate >= startDate;
      });
    }

    // Filter by end date
    if (filters.endDate) {
      filtered = filtered.filter(item => {
        if (!item.date && !item.createdAt) return false;
        
        const dateValue = item.date || item.createdAt;
        const itemDate = new Date(dateValue);
        const endDate = new Date(filters.endDate);
        
        itemDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        
        return itemDate <= endDate;
      });
    }

    setFilteredCashMovements(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      salesPerson: '',
      inventory: '',
      startDate: '',
      endDate: getTodayDate()
    });
    showNotification('Filters cleared', 'info');
  };

  const handleTypeChange = (type) => {
    setActiveType(type);
  };

  // ========== PAGINATION LOGIC ==========
  const totalPages = Math.ceil(filteredCashMovements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMovements = filteredCashMovements.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Calculate total amount for current filtered results
  const totalAmount = filteredCashMovements.reduce((sum, item) => {
    return sum + parseFloat(item.amount || 0);
  }, 0);

  if (!userRole) {
    return (
      <div className="inventory-main-content">
        <div className="cash-loading">Loading user information...</div>
      </div>
    );
  }

  // ========== RENDER ==========
  return (
    <main className="inventory-main-content">
      {/* Page Header */}
      <header className="inventory-content-header">
        <button onClick={toggleSidebar} className="sidebar-toggle-btn" type="button">
          ☰
        </button>
        <h1 className="inventory-content-title">Cash Management</h1>
        <div className="header-actions">
          
        </div>
      </header>

      {/* Type Filter Tabs */}
      <div className="cash-type-tabs">
        <button 
          className={`cash-type-tab ${activeType === 'CASH' ? 'active' : ''}`}
          onClick={() => handleTypeChange('CASH')}
        >
          <span className="tab-icon">💵</span>
          CASH
        </button>
        <button 
          className={`cash-type-tab ${activeType === 'COLLECTION' ? 'active' : ''}`}
          onClick={() => handleTypeChange('COLLECTION')}
        >
          <span className="tab-icon">📥</span>
          COLLECTION
        </button>
        <button 
          className={`cash-type-tab ${activeType === 'CHEQUE' ? 'active' : ''}`}
          onClick={() => handleTypeChange('CHEQUE')}
        >
          <span className="tab-icon">📄</span>
          CHEQUE
        </button>
      </div>

      {/* Filter Controls */}
      <div className="cash-filter-controls">
        <div className="cash-filter-group"> 
          <input
            type="text"
            name="search"
            placeholder="Search by description, reference, or code..."
            value={filters.search}
            onChange={handleFilterChange}
            className="cash-custom-input cash-search-input"
            style={{ flex: 1, minWidth: '250px' }}
          />

          <select
            name="salesPerson"
            value={filters.salesPerson}
            onChange={handleFilterChange}
            className="cash-custom-select"
          >
            <option value="">All Sales Persons</option>
            {uniqueSalesPersons.sort().map(person => (
              <option key={person} value={person}>{person}</option>
            ))}
          </select>

          <select
            name="inventory"
            value={filters.inventory}
            onChange={handleFilterChange}
            className="cash-custom-select"
          >
            <option value="">All Inventories</option>
            {uniqueInventories.sort().map(inv => (
              <option key={inv} value={inv}>{inv}</option>
            ))}
          </select>

          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
            className="cash-custom-input cash-date-input"
            placeholder="Start Date"
          />

          <input
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
            className="cash-custom-input cash-date-input"
            placeholder="End Date"
          />

          <button 
            className="cash-btn cash-btn-clear" 
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>

        {/* Filter Summary */}
        <div className="cash-filter-summary">
          <div>
            Showing <strong>{filteredCashMovements.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredCashMovements.length)}</strong> of{' '}
            <strong>{filteredCashMovements.length}</strong> {activeType.toLowerCase()} records
            {filteredCashMovements.length !== allCashMovements.length && (
              <span> (from <strong>{allCashMovements.length}</strong> total)</span>
            )}
          </div>
          <div className="cash-total-amount">
            Total Amount: <strong>Rs {totalAmount.toFixed(2)}</strong>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <CashTable
        cashMovements={paginatedMovements}
        isLoading={isLoading}
        activeType={activeType}
      />

      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button 
          className="btn-page" 
          disabled={currentPage === 1} 
          onClick={handlePreviousPage}
        >
          Previous
        </button>
        
        <div className="page-numbers">
          {currentPage > 2 && (
            <>
              <button className="btn-page-num" onClick={() => handlePageChange(1)}>1</button>
              {currentPage > 3 && <span className="page-ellipsis">...</span>}
            </>
          )}
          
          {currentPage > 1 && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(currentPage - 1)}
            >
              {currentPage - 1}
            </button>
          )}
          
          <button className="btn-page-num active">{currentPage}</button>
          
          {currentPage < totalPages && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(currentPage + 1)}
            >
              {currentPage + 1}
            </button>
          )}
          
          {currentPage < totalPages - 1 && (
            <>
              {currentPage < totalPages - 2 && <span className="page-ellipsis">...</span>}
              <button className="btn-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
            </>
          )}
        </div>

        <span className="page-info">Page {currentPage} of {totalPages || 1}</span>
        
        <button 
          className="btn-page" 
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={handleNextPage}
        >
          Next
        </button>
      </div>
    </main>
  );
};

// Helper function to get today's date in YYYY-MM-DD format
function getTodayDate() {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

export default Cash;